import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom'; // useLocationを追加

import { useAuth, useToast } from '../../contexts';
import { RegisterCredentials, AuthErrorType } from '../../types/auth';
import { UnifiedLoadingSpinner } from '../ui';

// Define RegisterFormData type
type RegisterFormData = RegisterCredentials;

interface RegisterFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  showTitle?: boolean;
}

const DEFAULT_BORDER_CLASS = 'border-gray-300';
const ERROR_BORDER_CLASS = 'border-red-500';

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onCancel, showTitle = true }) => {
  const location = useLocation(); // 追加
  const { register, error, clearError } = useAuth();
  const { showSuccess } = useToast();
  // ローカルのisLoading: フォーム送信中の状態を管理（AuthContextのグローバルisLoadingとは別目的）
  // AuthContext.isLoadingは認証状態の初期化や全体的な認証処理を管理
  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false); // パスワード表示/非表示状態
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // パスワード確認表示/非表示状態
  const [formData, setFormData] = useState<RegisterCredentials>({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<RegisterCredentials>>({});
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus on first input
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  // URLが変更されたときにエラーをクリア
  useEffect(() => {
    if (error) {
      clearError();
    }
    // フォームエラーもクリア
    setFormErrors({});
  }, [location.pathname, location.search, error, clearError]); // URLのパスまたはクエリパラメータが変更されたときに実行

  // フォームバリデーション
  const validateForm = (): boolean => {
    const errors: Partial<RegisterFormData> = {};

    if (!formData.email) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    if (!formData.username) {
      errors.username = 'ユーザー名を入力してください';
    } else if (formData.username.length < 2) {
      errors.username = 'ユーザー名は2文字以上で入力してください';
    } else if (formData.username.length > 50) {
      errors.username = 'ユーザー名は50文字以下で入力してください';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      errors.username = 'ユーザー名は英数字、アンダースコア、ハイフンのみ使用できます';
    }

    if (!formData.password) {
      errors.password = 'パスワードを入力してください';
    } else if (formData.password.length < 8) {
      errors.password = 'パスワードは8文字以上で入力してください';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'パスワードは大文字、小文字、数字を含む必要があります';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'パスワード確認を入力してください';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'パスワードが一致しません';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // リアルタイムバリデーション
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'email':
        if (!value) return 'メールアドレスを入力してください';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return '有効なメールアドレスを入力してください';
        break;
      case 'password':
        if (!value) return 'パスワードを入力してください';
        if (value.length < 8) return 'パスワードは8文字以上で入力してください';
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value))
          return 'パスワードは大文字、小文字、数字を含む必要があります';
        break;
      case 'confirmPassword':
        if (!value) return 'パスワード確認を入力してください';
        if (value !== formData.password) return 'パスワードが一致しません';
        break;
    }
    return undefined;
  };

  // 入力値変更ハンドラー
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // エラーをクリア
    if (formErrors[name as keyof RegisterFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
    if (error) {
      clearError();
    }
  };

  // フィールドのblurハンドラー（リアルタイムバリデーション用）
  const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const fieldError = validateField(name, value);
    if (fieldError) {
      setFormErrors(prev => ({
        ...prev,
        [name]: fieldError,
      }));
    }
  };

  // フォーム送信ハンドラー
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // フィールドレベルのエラー表示のみで十分
      return;
    }

    setIsLoading(true);
    try {
      const { confirmPassword, ...registerData } = formData;
      await register(registerData);
      showSuccess('アカウントが作成されました', '登録成功');
      onSuccess?.();
    } catch (err: any) {
      // 既存ユーザーの場合は適切なメッセージを表示してトップページに遷移
      if (err?.type === AuthErrorType.USER_ALREADY_EXISTS) {
        // 既存ユーザーの場合は、AuthContextでログイン処理が実行されるため、
        // ここでは追加のToast表示は行わない
        // 少し待ってからトップページに遷移
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
        return;
      }

      // 確認メール送信エラーの場合は成功メッセージを表示しない
      if (err?.type === AuthErrorType.EMAIL_SEND_FAILURE) {
        // エラーメッセージはAuthContextで設定されるので、ここでは何もしない
        return; // 早期リターンで成功処理を実行しない
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {showTitle && (
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">アカウント登録</h2>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
            メールアドレス
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleFieldBlur}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-blue-500 ${
              formErrors.email ? ERROR_BORDER_CLASS : DEFAULT_BORDER_CLASS
            }`}
            placeholder="example@email.com"
            disabled={isLoading}
            required
            data-testid="email-input"
          />
          {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
        </div>

        <div>
          <label htmlFor="username" className="block text-sm font-medium text-white mb-1">
            ユーザー名
          </label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            onBlur={handleFieldBlur}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-blue-500 ${
              formErrors.username ? ERROR_BORDER_CLASS : DEFAULT_BORDER_CLASS
            }`}
            placeholder="ユーザー名を入力"
            disabled={isLoading}
            required
            data-testid="username-input"
          />
          {formErrors.username && (
            <p className="mt-1 text-sm text-red-600">{formErrors.username}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-white mb-1">
            パスワード
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-blue-500 pr-10 ${
                formErrors.password ? ERROR_BORDER_CLASS : DEFAULT_BORDER_CLASS
              }`}
              placeholder="パスワードを入力"
              disabled={isLoading}
              required
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              data-testid="password-toggle-button"
            >
              {showPassword ? (
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {formErrors.password && (
            <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-white mb-1">
            パスワード確認
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-blue-500 pr-10 ${
                formErrors.confirmPassword ? ERROR_BORDER_CLASS : DEFAULT_BORDER_CLASS
              }`}
              placeholder="パスワードを再入力"
              disabled={isLoading}
              required
              data-testid="confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              data-testid="confirm-password-toggle-button"
            >
              {showConfirmPassword ? (
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {formErrors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
          )}
        </div>

        <div className="flex space-x-3 pt-12">
          <button
            type="submit"
            disabled={isLoading}
            data-testid="register-submit-button"
            className={`flex-1 py-2 px-4 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center ${
              isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isLoading ? (
              <>
                <UnifiedLoadingSpinner size="sm" color="white" variant="spinner" className="mr-2" />
                <span role="status">処理中</span>
              </>
            ) : (
              '登録'
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              キャンセル
            </button>
          )}
        </div>
      </form>

      <div className="mt-6">
        {/* <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">または</span>
          </div>
        </div>

        <SocialLoginSection /> */}
      </div>
    </div>
  );
};

export default RegisterForm;
